import { useEffect, useState } from "react";
import { ProfileIdentitySection } from "./profile/ProfileIdentitySection";
import { ProfileAmenitiesSection } from "./profile/ProfileAmenitiesSection";
import { ProfileVisualAssetsSection } from "./profile/ProfileVisualAssetsSection";
import { ProfileLinksSection } from "./profile/ProfileLinksSection";
import { confirmDiscardUnsaved } from "./confirmDiscardUnsaved";
import "./ListingsProfileTab.scss";

type ProfileSectionKey = "identity" | "amenities" | "visual-assets" | "links";

const SECTIONS: { key: ProfileSectionKey; label: string }[] = [
  { key: "identity", label: "Identity" },
  { key: "amenities", label: "Amenities" },
  { key: "visual-assets", label: "Visual Assets" },
  { key: "links", label: "Links" },
];

interface Props {
  locationId?: number;
  onDirtyChange?: (dirty: boolean) => void;
}

export function ListingsProfileTab({ locationId, onDirtyChange }: Props) {
  const [active, setActive] = useState<ProfileSectionKey>("identity");
  const [dirty, setDirty] = useState(false);

  // Bubble the active section's dirty state up so the parent tab nav can guard too.
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // Warn before a full page unload (refresh / close) while there are pending edits.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  function selectSection(key: ProfileSectionKey) {
    if (key === active) return;
    if (dirty) {
      confirmDiscardUnsaved(() => {
        setDirty(false);
        setActive(key);
      });
      return;
    }
    setActive(key);
  }

  return (
    <div className="listings-profile">
      <aside className="lp-sidenav">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`lp-sidenav-item${active === s.key ? " active" : ""}`}
            onClick={() => selectSection(s.key)}
          >
            {s.label}
          </button>
        ))}
      </aside>

      <div className="lp-content">
        {active === "identity" && (
          <ProfileIdentitySection locationId={locationId} onDirtyChange={setDirty} />
        )}
        {active === "amenities" && (
          <ProfileAmenitiesSection locationId={locationId} onDirtyChange={setDirty} />
        )}
        {active === "visual-assets" && (
          <ProfileVisualAssetsSection locationId={locationId} onDirtyChange={setDirty} />
        )}
        {active === "links" && (
          <ProfileLinksSection locationId={locationId} onDirtyChange={setDirty} />
        )}
      </div>
    </div>
  );
}
